import React from "react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import {
  ProductBreadcrumb,
  ProductHero,
  ProductGallery,
  QuickSpecsTabs,
  KeyFeatures,
  OptionalAccessories,
  Applications,
  WhyChooseLiftile,
  QualitySafetyCommitment,
  DownloadBrochure,
  PremiumFAQ,
  RelatedProducts,
  ProductFinalCTA,
} from "../components/product";
import {
  ArrowUp,
  ArrowUpDown,
  Award,
  BadgeCheck,
  Building2,
  Cable,
  CircuitBoard,
  ClipboardCheck,
  Cog,
  Factory,
  Flag,
  Frame,
  HardHat,
  Headphones,
  Landmark,
  Layers,
  Lock,
  Package,
  Paintbrush,
  Radio,
  Rocket,
  ShieldCheck,
  Siren,
  Sparkles,
  Wind,
  Wrench,
  Zap,
} from "lucide-react";

/* =====================================================================
   ROPE SUSPENDED PLATFORM (LRP10X) — dedicated product page.
   Composed entirely from /components/product/* shared primitives.
   Only product-specific data lives in this file.
   ===================================================================== */

const HERO_IMAGE = "/product-suspended-platform.jpg";

const HERO_SPECS = [
  { Icon: ArrowUp, value: "Up to 130 m", label: "Maximum Height" },
  { Icon: Package, value: "1000 kg", label: "Gross Load Capacity" },
  { Icon: Zap, value: "10–12 m/min", label: "Platform Speed" },
  { Icon: Flag, value: "Made in India", label: "Engineered & Manufactured" },
];

const HERO_BADGES = [
  "Up to 130 Mtr Height",
  "Premium Components",
  "Owner Inspected",
  "Made in India",
];

const GALLERY = [
  {
    src: "/product-suspended-platform.jpg",
    caption:
      "LIFTILE LRP10X Rope Suspended Platform — engineered for safe work at height",
  },
];

const QUICK_SPECS = [
  { label: "Gross Load", value: "1000 kg" },
  { label: "Maximum Height", value: "130 m" },
  { label: "Platform Speed", value: "10–12 m/min" },
  { label: "Boom Length", value: "6.5 ft" },
];

const MODELS = {
  LRP10X: {
    name: "LRP10X",
    tagline:
      "Heavy-duty rope suspended platform for high-rise façade & maintenance work",
    rows: [
      { label: "Model", value: "LRP10X" },
      { label: "Gross Load", value: "1000 kg" },
      { label: "Wire Rope", value: "9.1 mm" },
      { label: "Maximum Height", value: "130 m" },
      { label: "Platform Speed", value: "10–12 m/min" },
      { label: "Boom Length", value: "6.5 ft" },
      {
        label: "Operation",
        value: "Panel Box with Wired Remote Control",
      },
      { label: "Power Supply", value: "Three Phase" },
    ],
  },
};

const FEATURES = [
  {
    Icon: Rocket,
    title: "Quick & Easy Installation",
    desc: "Modular design and thoughtful engineering enable fast site setup, saving valuable project time on every deployment.",
  },
  {
    Icon: Layers,
    title: "Heavy Duty Modular Platform",
    desc: "High-strength steel platform engineered for stability, safety and long-term reliability at working heights.",
  },
  {
    Icon: Sparkles,
    title: "Hot Dip Galvanized Structure for Superior Corrosion Resistance",
    desc: "Hot dip galvanized steel structure delivers long-lasting corrosion protection for demanding on-site and coastal conditions.",
  },
  {
    Icon: Cable,
    title: "Certified Usha Martin Wire Rope",
    desc: "ISI-certified Usha Martin 9.1 mm wire rope for superior strength, durability and dependable suspension.",
  },
  {
    Icon: Cog,
    title: "Heavy Duty Suspension Mechanism",
    desc: "Precision-engineered suspension system delivers stable, controlled vertical movement even under full load.",
  },
  {
    Icon: Radio,
    title: "Wired Remote Control",
    desc: "Responsive wired remote control enables safe, precise operation of the platform from the working surface.",
  },
  {
    Icon: Siren,
    title: "Emergency Stop System",
    desc: "Dedicated emergency stop halts platform movement instantly during unsafe or unexpected conditions.",
  },
  {
    Icon: ArrowUpDown,
    title: "Smooth Vertical Movement",
    desc: "Well-tuned drive and suspension geometry deliver smooth, judder-free platform travel for operator confidence.",
  },
  {
    Icon: ClipboardCheck,
    title: "Owner Inspected Before Dispatch",
    desc: "Every LRP10X is personally inspected and quality signed off before leaving the LIFTILE factory floor.",
  },
  {
    Icon: BadgeCheck,
    title: "Third Party Test Ready",
    desc: "Engineered and documented for third-party load and safety testing where required by site or regulatory norms.",
  },
  {
    Icon: Lock,
    title: "Heavy Duty Safety Lock",
    desc: "Robust safety-lock mechanism engages automatically to protect operators against uncontrolled descent.",
  },
  {
    Icon: Award,
    title: "Premium Branded Components",
    desc: "Manufactured using trusted branded electricals and hardware including Usha Martin, ABB and Schneider Electric.",
  },
];

const ACCESSORIES = [
  {
    Icon: Layers,
    title: "Different Platform Lengths",
    desc: "Standard and extended platform options to match specific building geometries and façade layouts.",
  },
  {
    Icon: Frame,
    title: "Additional Suspension Brackets",
    desc: "Extra rooftop suspension bracket sets for complex façade profiles and multi-drop project configurations.",
  },
  {
    Icon: Cable,
    title: "Extra Wire Rope Length",
    desc: "Additional certified Usha Martin 9.1 mm wire rope for taller working heights up to 130 m.",
  },
  {
    Icon: Lock,
    title: "Safety Lock",
    desc: "Independent secondary safety-lock unit for redundant fall-arrest protection during façade operations.",
  },
  {
    Icon: CircuitBoard,
    title: "Electrical Control Panel",
    desc: "IP-rated electrical control panel with premium ABB / Schneider Electric switchgear and contactors.",
  },
  {
    Icon: Package,
    title: "Material Hoisting Attachment",
    desc: "Optional attachment for lifting tools, glass panels and finishing materials safely along with the platform.",
  },
];

const APPLICATIONS = [
  {
    Icon: ArrowUp,
    title: "High Rise Buildings",
    desc: "Safe access solution for façade, glazing, cleaning and maintenance on tall residential and commercial towers.",
  },
  {
    Icon: Frame,
    title: "Glass Installation",
    desc: "Stable working platform for glass panel handling and precision curtain-wall installation.",
  },
  {
    Icon: Paintbrush,
    title: "Exterior Painting",
    desc: "Ideal platform for exterior painting, coating and finishing works on multi-storey buildings.",
  },
  {
    Icon: Wrench,
    title: "Building Maintenance",
    desc: "Routine maintenance, repair and inspection of building exteriors, joints and structural elements.",
  },
  {
    Icon: Wind,
    title: "Façade Cleaning",
    desc: "Professional access platform for glass, ACP and stone façade cleaning across tall buildings.",
  },
  {
    Icon: Factory,
    title: "Industrial Chimneys",
    desc: "Safe vertical access for inspection, repair and maintenance of tall industrial chimneys and stacks.",
  },
  {
    Icon: Landmark,
    title: "Bridge Inspection",
    desc: "Reliable access platform for bridge inspection, maintenance and structural repair work.",
  },
  {
    Icon: Building2,
    title: "Commercial Buildings",
    desc: "Trusted access platform for office towers, malls, hotels and mixed-use commercial developments.",
  },
  {
    Icon: Layers,
    title: "Infrastructure Projects",
    desc: "Proven performance on metros, treatment plants, power plants and public infrastructure works.",
  },
];

const TRUST_CARDS = [
  {
    Icon: ShieldCheck,
    title: "Premium Safety",
    desc: "Multi-layer safety systems including safety lock, emergency stop and heavy-duty suspension design.",
  },
  {
    Icon: BadgeCheck,
    title: "Reliable Performance",
    desc: "Trial-run tested with load verification for dependable performance project after project.",
  },
  {
    Icon: HardHat,
    title: "Heavy Duty Structure",
    desc: "Precision-welded steel platform engineered for demanding conditions and long service life.",
  },
  {
    Icon: Award,
    title: "Premium Components",
    desc: "Trusted branded electricals, wire rope and hardware from Usha Martin, ABB and Schneider Electric.",
  },
  {
    Icon: Flag,
    title: "Made in India",
    desc: "Designed and manufactured in India for Indian construction and infrastructure conditions.",
  },
  {
    Icon: Rocket,
    title: "Easy Installation",
    desc: "Modular design enables fast site setup, saving valuable project time on every deployment.",
  },
  {
    Icon: Headphones,
    title: "Nationwide Support",
    desc: "Installation guidance, spare parts and after-sales service across every project geography in India.",
  },
];

const TECH_HIGHLIGHTS = [
  {
    Icon: Cog,
    title: "Heavy Duty Suspension System",
    desc: "Precision-engineered rooftop bracket + boom + winch assembly for stable, controlled platform suspension.",
  },
  {
    Icon: Cable,
    title: "Certified Wire Rope",
    desc: "ISI-certified Usha Martin 9.1 mm wire rope delivers superior strength and safety at working heights.",
  },
  {
    Icon: Layers,
    title: "Stable Working Platform",
    desc: "Heavy-duty steel platform engineered for even load distribution and rock-steady vertical movement.",
  },
  {
    Icon: CircuitBoard,
    title: "Reliable Control System",
    desc: "Premium ABB / Schneider Electric electricals housed in an IP-rated panel with wired remote operation.",
  },
  {
    Icon: Wrench,
    title: "Precision Engineering",
    desc: "Fabricated to strict tolerances with owner inspection and third-party test-ready documentation.",
  },
  {
    Icon: BadgeCheck,
    title: "Long Service Life",
    desc: "Premium materials, hot dip galvanized corrosion protection and quality components ensure years of dependable use.",
  },
];

const FAQS = [
  {
    q: "What is the maximum operating height of the LRP10X?",
    a: "The LIFTILE LRP10X Rope Suspended Platform is engineered to safely operate up to 130 metres — suitable for the tallest façade, maintenance and finishing applications.",
  },
  {
    q: "What is the rated load capacity?",
    a: "The LRP10X has a gross load capacity of 1000 kg, providing safe working capacity for operators, tools and materials during façade and maintenance operations.",
  },
  {
    q: "How is the platform installed on a building?",
    a: "The LRP10X uses a modular design with rooftop suspension brackets, a 6.5 ft boom assembly and certified wire rope. Installation is quick and does not require a permanent structural foundation on the building.\n\nOur team provides installation guidance and site commissioning support to ensure safe setup on every project.",
  },
  {
    q: "What safety features are included?",
    a: "The LRP10X includes a heavy-duty safety lock, emergency stop system, certified Usha Martin wire rope, heavy-duty suspension mechanism, IP-rated electrical panel and wired remote control.\n\nEvery machine is owner inspected before dispatch and engineered to be third-party test ready where required.",
  },
  {
    q: "What maintenance does the platform require?",
    a: "Routine inspection of the wire rope, safety lock, brake motor, suspension brackets and electrical components — plus periodic lubrication of moving parts — is recommended for reliable long-term operation.",
  },
  {
    q: "What type of wire rope is used?",
    a: "The LRP10X uses ISI-certified Usha Martin 9.1 mm wire rope — one of the most trusted premium wire ropes available for suspended access platforms.",
  },
  {
    q: "What are the power requirements?",
    a: "The LRP10X operates on Three-Phase 415 V AC, 50 Hz power supply — the standard site electrical supply used across Indian construction projects.",
  },
  {
    q: "Does LIFTILE provide service and support?",
    a: "Yes.\n\nLIFTILE provides installation guidance, spare parts, technical assistance and after-sales service across India through our nationwide support network.",
  },
];

const RELATED_PRODUCTS = [
  {
    slug: "passenger-cum-material-hoist",
    name: "Passenger Cum Material Hoist",
    desc: "LPMH Series twin-cage hoist for safe transport of personnel and materials up to 150 m on high-rise sites.",
    image: "/product-passenger-hoist.jpg",
  },
  {
    slug: "conventional-tower-hoist",
    name: "Conventional Tower Hoist",
    desc: "LTC Series wire-rope tower hoist for dependable material lifting up to 65 m on residential and industrial projects.",
    image: "/conventional-tower-hoist-site-2.jpeg",
  },
  {
    slug: "monkey-lift",
    name: "Monkey Lift",
    desc: "Compact LML250 mini material lifter — ideal for slab-level material transport on tight sites and finishing phases.",
    image: "/product-monkey-lift.jpg",
  },
];

const CTA_BADGES = [
  "Up to 130 Mtr Height",
  "Premium Components",
  "Owner Inspected",
  "Made in India",
];

/* ---------- Blueprint SVG for Download Brochure ---------- */
const LRP_BLUEPRINT_SVG = (
  <svg
    aria-hidden
    viewBox="0 0 400 400"
    className="absolute right-0 top-1/2 -translate-y-1/2 w-[280px] md:w-[360px] h-auto opacity-[0.14] pointer-events-none"
    fill="none"
    stroke="#FF6B1A"
    strokeWidth="1.2"
  >
    {/* Building outline (right side) */}
    <rect x="260" y="20" width="80" height="360" strokeDasharray="0" />
    {/* Building floor lines */}
    {[70, 120, 170, 220, 270, 320].map((y) => (
      <line key={y} x1="260" y1={y} x2="340" y2={y} strokeDasharray="3 3" opacity="0.6" />
    ))}
    {/* Rooftop suspension bracket / boom */}
    <line x1="200" y1="30" x2="270" y2="30" strokeWidth="2" />
    <line x1="200" y1="30" x2="200" y2="60" />
    <line x1="200" y1="60" x2="270" y2="30" strokeDasharray="2 3" opacity="0.6" />
    <circle cx="200" cy="30" r="6" />
    {/* Suspension cables to platform */}
    <line x1="200" y1="35" x2="130" y2="240" strokeDasharray="3 3" />
    <line x1="200" y1="35" x2="180" y2="240" strokeDasharray="3 3" />
    {/* Working platform */}
    <rect x="120" y="240" width="70" height="18" />
    {/* Platform railing */}
    <line x1="120" y1="240" x2="120" y2="230" />
    <line x1="190" y1="240" x2="190" y2="230" />
    <line x1="120" y1="230" x2="190" y2="230" />
    {/* Winch box under platform */}
    <rect x="140" y="258" width="30" height="15" strokeDasharray="2 2" />
    {/* Dimension line height 130m */}
    <line x1="60" y1="30" x2="60" y2="360" strokeDasharray="2 4" />
    <text
      x="30"
      y="200"
      fill="#FF6B1A"
      fontSize="10"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      130m
    </text>
    {/* Boom length label */}
    <text
      x="205"
      y="55"
      fill="#FF6B1A"
      fontSize="9"
      fontFamily="Montserrat"
      opacity="0.7"
    >
      6.5 ft
    </text>
    {/* Ground line */}
    <line x1="60" y1="380" x2="340" y2="380" strokeDasharray="4 4" opacity="0.6" />
  </svg>
);

export default function RopeSuspendedPlatformPage() {
  return (
    <>
      <Navbar />
      <ProductBreadcrumb productName="Rope Suspended Platform (LRP10X)" />

      {/* ─── SECTION 1: HERO ─── */}
      <ProductHero
        testId="lrp-hero"
        backgroundImage={HERO_IMAGE}
        eyebrow="LRP10X Series"
        title={
          <>
            LIFTILE Rope
            <br />
            Suspended{" "}
            <span className="text-liftile-orange">Platform</span>
          </>
        }
        subtitle="Engineered for safe, stable and efficient work at height — designed for high-rise construction, façade work, maintenance and exterior finishing applications."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{ label: "Download Brochure" }}
        specs={HERO_SPECS}
        badges={HERO_BADGES}
      />

      {/* ─── SECTION 2: GALLERY ─── */}
      <ProductGallery
        testId="lrp-gallery"
        heading={
          <>
            Rope Suspended Platform{" "}
            <span className="text-liftile-orange">In Action</span>
          </>
        }
        subtitle="A closer look at the LIFTILE LRP10X — from precision fabrication at our factory to real high-rise applications across India."
        items={GALLERY}
        seriesLabel="LRP10X Series"
        imageAlt="LIFTILE LRP10X Rope Suspended Platform"
      />

      {/* ─── SECTION 3: QUICK SPECS (split layout with 4 summary cards) ─── */}
      <QuickSpecsTabs
        testId="lrp-quick-specs"
        heading={
          <>
            Quick <span className="text-liftile-orange">Specifications</span>
          </>
        }
        subtitle="Everything you need to know about the LRP10X — engineered for safe access work at heights up to 130 metres."
        quickSpecs={QUICK_SPECS}
        models={MODELS}
        defaultModel="LRP10X"
        footerNote={null}
      />

      {/* ─── SECTION 4: KEY FEATURES ─── */}
      <KeyFeatures
        testId="lrp-features"
        heading={
          <>
            Built for Safety.{" "}
            <br className="hidden sm:block" />
            <span className="text-liftile-orange">
              Engineered for Reliability.
            </span>
          </>
        }
        subtitle="Every LIFTILE LRP10X Rope Suspended Platform is engineered with premium branded components and rigorous quality inspections to deliver dependable performance on demanding high-rise projects."
        items={FEATURES}
      />

      {/* ─── SECTION 5: OPTIONAL ACCESSORIES ─── */}
      <OptionalAccessories
        testId="lrp-accessories"
        heading={
          <>
            Optional <span className="text-liftile-orange">Accessories</span>
          </>
        }
        subtitle="Configure the LRP10X for your specific façade, maintenance or high-rise access requirements with our range of premium accessories."
        items={ACCESSORIES}
        gridCols="lg:grid-cols-3"
      />

      {/* ─── SECTION 6: APPLICATIONS ─── */}
      <Applications
        testId="lrp-applications"
        heading={
          <>
            Ideal for Every{" "}
            <span className="text-liftile-orange">Access Application</span>
          </>
        }
        subtitle="From high-rise façade work to industrial chimney maintenance and bridge inspection — the LRP10X delivers dependable access performance across a wide range of vertical work environments."
        items={APPLICATIONS}
        variant="dark"
        gridCols="lg:grid-cols-3"
      />

      {/* ─── SECTION 7: WHY CHOOSE LIFTILE ─── */}
      <WhyChooseLiftile
        testId="lrp-why-choose"
        image="/product-suspended-platform.jpg"
        imageAlt="LIFTILE LRP10X Rope Suspended Platform — engineered for safe work at height"
        imageCaption="LRP10X — engineered for safe, stable and efficient work at every height."
        floatingBadge={{ top: "Up to", bottom: "130 metres" }}
        eyebrow="Why LRP10X"
        heading={
          <>
            Built for Modern <br className="hidden sm:block" />
            <span className="text-liftile-orange">High-Rise Access</span>
          </>
        }
        subtitle="The LRP10X combines multi-layer safety systems, premium branded components and heavy-duty construction to deliver dependable access solutions for modern façade, maintenance and high-rise construction projects."
        trustCards={TRUST_CARDS}
      />

      {/* ─── SECTION 8: TECHNICAL HIGHLIGHTS ─── */}
      <QualitySafetyCommitment
        testId="lrp-tech-highlights"
        eyebrow="Technical Highlights"
        heading={
          <>
            Precision Engineering.{" "}
            <span className="text-liftile-orange">Premium Components.</span>
          </>
        }
        subtitle="A closer look at the engineering foundations that make the LRP10X a safe, stable and reliable rope suspended platform."
        items={TECH_HIGHLIGHTS}
      />

      {/* ─── SECTION 9: DOWNLOAD BROCHURE ─── */}
      <DownloadBrochure
        testId="lrp-brochure"
        heading={
          <>
            Download Product{" "}
            <span className="text-liftile-orange">Brochure</span>
          </>
        }
        subtitle="Download the complete LRP10X technical catalogue — including specifications, safety systems, drawings and configuration options."
        blueprintSvg={LRP_BLUEPRINT_SVG}
      />

      {/* ─── SECTION 10: FAQ ─── */}
      <PremiumFAQ
        testId="lrp-faq"
        heading={
          <>
            Frequently Asked{" "}
            <span className="text-liftile-orange">Questions</span>
          </>
        }
        subtitle="Everything builders, contractors and facility engineers most often ask us about the LIFTILE Rope Suspended Platform."
        items={FAQS}
      />

      {/* ─── SECTION 11: RELATED PRODUCTS ─── */}
      <RelatedProducts
        testId="lrp-related"
        heading={
          <>
            Explore More{" "}
            <span className="text-liftile-orange">LIFTILE Solutions</span>
          </>
        }
        items={RELATED_PRODUCTS}
      />

      {/* ─── SECTION 12: FINAL CTA ─── */}
      <ProductFinalCTA
        testId="lrp-product-cta"
        backgroundImage="/project-thiruvananthapuram.png"
        backgroundAlt="LIFTILE Rope Suspended Platform on a live high-rise project"
        heading={
          <>
            Work Safely at{" "}
            <span className="text-liftile-orange">Every Height.</span>
          </>
        }
        subtitle="The LIFTILE Rope Suspended Platform is designed to deliver reliable access solutions with superior safety, durability and performance for modern construction projects."
        primaryCta={{ label: "Request a Quote", href: "/contact" }}
        secondaryCta={{
          label: "Contact Engineering Team",
          href: "/contact",
        }}
        trustBadges={CTA_BADGES}
      />

      <Footer />
    </>
  );
}
